import React from 'react';

/** User-facing copy for generic / technical failure dialogs */
export const GENERIC_ERROR_MODAL_TITLE = 'Technical Issues';
export const GENERIC_ERROR_MODAL_MESSAGE =
  "We're currently experiencing technical issues. We will contact you within 48 hours. If you'd like to connect with us sooner, please call (425) 640-7009";

interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  onGoBack?: () => void;
}

/**
 * ErrorModal - A modal component for displaying error messages.
 *
 * Displays a centered modal with error information and a close button.
 * Includes proper accessibility attributes and keyboard navigation.
 *
 * @param isOpen - Whether the modal is visible
 * @param onClose - Function to call when modal should be closed
 * @param title - Error title/heading
 * @param message - Error message content
 */
const ErrorModal: React.FC<ErrorModalProps> = ({ isOpen, onClose, title, message, onGoBack }) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="error-modal-backdrop"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="error-modal-title"
      aria-describedby="error-modal-message"
    >
      <div
        className="error-modal-content"
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '400px',
          width: '100%',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666',
            padding: '4px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px'
          }}
          aria-label="Close error modal"
        >
          ×
        </button>

        {/* Error icon */}
        <div
          style={{
            fontSize: '48px',
            color: '#DE5829',
            textAlign: 'center',
            marginBottom: '16px'
          }}
        >
          ⚠️
        </div>

        {/* Error title */}
        <h2
          id="error-modal-title"
          style={{
            color: '#DE5829',
            fontSize: '1.25rem',
            fontWeight: '700',
            marginBottom: '12px',
            textAlign: 'center',
            marginTop: 0
          }}
        >
          {title}
        </h2>

        {/* Error message */}
        <p
          id="error-modal-message"
          style={{
            color: '#333',
            fontSize: '1rem',
            lineHeight: '1.5',
            marginBottom: '24px',
            textAlign: 'center',
            marginTop: 0
          }}
        >
          {message}
        </p>

        {/* Action buttons */}
        <div style={{ textAlign: 'center', display: 'flex', gap: '12px', justifyContent: 'center' }}>
          {onGoBack && (
            <button
              onClick={onGoBack}
              style={{
                backgroundColor: '#DE5829',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                minWidth: '120px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#C44A1F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#DE5829';
              }}
            >
              Go Back
            </button>
          )}
          {!onGoBack && (
            <button
              onClick={onClose}
              style={{
                backgroundColor: '#DE5829',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                minWidth: '120px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#C44A1F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#DE5829';
              }}
            >
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorModal;
